import React, { useState } from "react";
import api, { formatTL, formatDuration, formatDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveQuery, invalidate, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Plate } from "@/components/Plate";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BusinessTagSelect } from "@/components/BusinessTag";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function History() {
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.role === "super_admin";
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [editing, setEditing] = useState(null);

  const { data, refetch } = useLiveQuery(
    [TAGS.SESSIONS, TAGS.UNPAID],
    async () => (await api.get(`/sessions${appliedQ ? `?plate=${appliedQ}` : ""}`)).data,
    { deps: [appliedQ] },
  );
  const rows = data || [];

  const runSearch = () => setAppliedQ(q);

  const deleteSession = async (s) => {
    if (!window.confirm(
      `Bu oturumu silmek istediğinize emin misiniz?\n\n` +
      `Plaka: ${s.plate}\nÜcret: ${formatTL(s.fee)}\nDurum: ${s.status} / ${s.payment_status}\n\n` +
      `⚠️ Ödenmiş oturum silinirse Kasa/Banka toplamları etkilenir.\nBu işlem geri alınamaz.`
    )) return;
    try {
      await api.delete(`/sessions/${s.id}`);
      toast.success("Oturum silindi");
      invalidate([TAGS.SESSIONS, TAGS.UNPAID, TAGS.KASA]);
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/sessions/${editing.id}`, {
        plate: editing.plate,
        fee: Number(editing.fee),
        payment_status: editing.payment_status,
        status: editing.status,
        note: editing.note,
        parking_lot_key: editing.parking_lot_key,
      });
      toast.success("Oturum güncellendi — Kasa & Tahsilatlar otomatik yenilendi");
      setEditing(null);
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex gap-2">
        <Input
          data-testid="history-search-input"
          placeholder="Plaka ile ara (örn: 34ABC)"
          value={q}
          onChange={(e) => setQ(e.target.value.toUpperCase())}
          className="rounded-sm max-w-md font-plate uppercase"
          onKeyDown={(e) => e.key === "Enter" && runSearch()}
        />
        <Button data-testid="history-search-btn" onClick={runSearch} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90">Ara</Button>
      </div>

      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plaka</TableHead>
                <TableHead>Otopark</TableHead>
                <TableHead>Giriş</TableHead>
                <TableHead>Çıkış</TableHead>
                <TableHead>Süre</TableHead>
                <TableHead>Ücret</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Ödeme</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Kayıt yok</TableCell></TableRow>
              )}
              {rows.map((s) => (
                <TableRow key={s.id}>
                  <TableCell><Plate country={s.country_code}>{s.plate}</Plate></TableCell>
                  <TableCell className="uppercase text-xs tracking-widest">{s.parking_lot_key === "ust_kat" ? "Üst" : "Alt"}</TableCell>
                  <TableCell className="text-xs">{formatDateTime(s.entry_time)}</TableCell>
                  <TableCell className="text-xs">{s.exit_time ? formatDateTime(s.exit_time) : "—"}</TableCell>
                  <TableCell className="font-mono">{s.duration_minutes != null ? formatDuration(s.duration_minutes) : "—"}</TableCell>
                  <TableCell className="font-mono">{formatTL(s.fee)}</TableCell>
                  <TableCell>
                    <Badge variant={s.status === "active" ? "default" : "secondary"} className="rounded-sm uppercase text-[10px] tracking-widest">
                      {s.status === "active" ? "Aktif" : "Kapalı"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={s.payment_status === "odendi" ? "outline" : s.payment_status === "muaf" ? "outline" : "destructive"}
                      className="rounded-sm uppercase text-[10px] tracking-widest"
                    >
                      {s.payment_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing({ ...s })}
                        data-testid={`edit-session-${s.id}`}
                      >
                        Düzenle
                      </Button>
                      {isSuperAdmin && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => deleteSession(s)}
                          data-testid={`delete-session-${s.id}`}
                          className="rounded-sm text-destructive h-8 w-8 p-0"
                          title="Süperadmin: Oturumu tamamen sil">
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Oturum Düzenle</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Plaka</Label>
                <Input className="rounded-sm font-plate uppercase" value={editing.plate} onChange={(e) => setEditing({ ...editing, plate: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <Label>Ücret (₺)</Label>
                <Input type="number" step="0.01" className="rounded-sm" value={editing.fee} onChange={(e) => setEditing({ ...editing, fee: e.target.value })} />
              </div>
              <div>
                <Label>Otopark</Label>
                {/* Manuel taşıma: yanlış otoparka düşmüş kayıtları buradan
                    doğru lot'a çekebilirsin (kaydı silmez, yerinde günceller). */}
                <Select
                  value={editing.parking_lot_key}
                  onValueChange={(v) => setEditing({ ...editing, parking_lot_key: v })}
                >
                  <SelectTrigger
                    className="rounded-sm"
                    data-testid="edit-parking-lot-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alt_kat">Alt Kat</SelectItem>
                    <SelectItem value="ust_kat">Üst Kat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>İşletme Müşterisi</Label>
                <BusinessTagSelect
                  session={editing}
                  size="md"
                  className="w-full"
                  onUpdated={(u) => setEditing({ ...editing, ...u, fee: u.fee })}
                />
                <div className="text-[10px] text-muted-foreground mt-1">Seçim anında kaydedilir ve ücret yeniden hesaplanır.</div>
              </div>
              <div>
                <Label>Durum</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="closed">Kapalı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ödeme Durumu</Label>
                <Select value={editing.payment_status} onValueChange={(v) => setEditing({ ...editing, payment_status: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bekliyor">Bekliyor</SelectItem>
                    <SelectItem value="odendi">Ödendi</SelectItem>
                    <SelectItem value="muaf">Muaf</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Not</Label>
                <Input className="rounded-sm" value={editing.note || ""} onChange={(e) => setEditing({ ...editing, note: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setEditing(null)}>İptal</Button>
                <Button data-testid="edit-save-btn" onClick={saveEdit} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">Kaydet</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
